import { useState, useEffect } from 'react';
import axios from 'axios';
import storageService from '@/lib/storage-service';
import { CACHE_KEY_PERMISSIONS, CACHE_KEY_USER_INFO } from '@/constants/storage.constants';
import { fetchPermission, fetchSingleUser } from '@/pages/UserManagement/UserList/userApi';
import { jwtDecode } from 'jwt-decode';

type PermifyMap = Record<string, 'CHECK_RESULT_ALLOWED' | 'CHECK_RESULT_DENIED'>;

type AdditionalInformation = {
  sub?: string;
  role_type?: string;
  preferred_username?: string;
  given_name?: string;
  createdAt?: number;
  phoneNumber?: string;
  emr_project_membership_id?: string;
  medplum_id?: string;
  emr_id?: string;
  permify_map?: PermifyMap;
  family_name?: string;
  email?: string;
  permify_id?: string;
  updatedAt?: number;
};

export type UserInfo = {
  firstName?: string;
  lastName?: string;
  additionalInformation?: AdditionalInformation;
  family_name?: string;
  given_name?: string;
  displayName?: string;
  externalId?: string;
  accountNonExpired?: boolean;
  id?: string;
  email?: string;
  enabled?: boolean;
  username?: string;
  accountNonLocked?: boolean;
  sub?: string;
};

type UseUserInfoResult = {
  userInfo: UserInfo | null;
  userPermissions: PermifyMap;
  loading: boolean;
  error: string | null; 
};

export const useUserInfo = (): UseUserInfoResult => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [userPermissions, setUserPermissions] = useState<PermifyMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = storageService.getAccessToken();

  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY_USER_INFO);
    localStorage.removeItem(CACHE_KEY_PERMISSIONS);
  };

  useEffect(() => {
    const fetchAndCacheUser = async (id: string) => {
      try {
        const [userResponse, permissionsResponse] = await Promise.all([
          fetchSingleUser(id),
          fetchPermission(id),
        ]);

        const userData = userResponse?.usersData ?? null;
        const permissionsData = permissionsResponse?.permissions ?? {};

        setUserInfo(userData);
        setUserPermissions(permissionsData);

        localStorage.setItem(CACHE_KEY_USER_INFO, JSON.stringify(userData));
        localStorage.setItem(CACHE_KEY_PERMISSIONS, JSON.stringify(permissionsData));
      } catch (err) {
        clearCache();
        setError(
          axios.isAxiosError(err)
            ? err.response?.data?.message || err.message
            : 'An unknown error occurred'
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      try {
        const { sub } = jwtDecode<{ sub: string }>(token);

        const cachedUser = localStorage.getItem(CACHE_KEY_USER_INFO);
        const cachedPermissions = localStorage.getItem(CACHE_KEY_PERMISSIONS);

        if (cachedUser && cachedPermissions) {
          setUserInfo(JSON.parse(cachedUser));
          setUserPermissions(JSON.parse(cachedPermissions));
          setLoading(false);
        } else {
          fetchAndCacheUser(sub);
        }
      } catch {
        clearCache();
        setLoading(false);
        setError('Invalid token');
      }
    } else {
      clearCache();
      setLoading(false);
    }
  }, [token]);
  return { userInfo, userPermissions, loading, error };
};

export default useUserInfo;
